<script setup>
import { ref, computed, watch } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import AudienceService from '@/services/AudienceService'
import Alert from '@/components/Atoms/Alert.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'

// The email capture half of the Audience settings card: which viewers get the
// form, what it says, and where each capture is delivered.
//
// One explicit Save for the whole block, unlike the card's instant toggles:
// the block is a form — a mode plus the copy that goes with it — and saving
// per keystroke or per control would publish half-written text to viewers.
// Drafting everything and validating at the Save click keeps what viewers
// see coherent.

const { notify } = useNotification()

const props = defineProps({
	streamId: {
		type: String,
		required: true
	},
	/** The saved `emailCapture` block, possibly absent on older streams. */
	emailCapture: {
		type: Object,
		default: null
	},
	/** The parent's persist() in flight. */
	saving: {
		type: Boolean,
		default: false
	}
})

const emit = defineEmits(['save'])

const MODES = [
	{
		key: 'off',
		label: 'Off',
		hint: 'The form never appears in the player.'
	},
	{
		key: 'optional',
		label: 'Optional',
		hint: 'Viewers are invited to leave an email, but can dismiss the form and keep watching and chatting.'
	},
	{
		key: 'required_chat',
		label: 'Required to chat',
		hint: 'Viewers must leave an email before posting in chat or Q&A. Watching and reading chat are never blocked.'
	},
	{
		key: 'required_watch',
		label: 'Required to watch',
		hint: 'Shows a gate before playback: the stream does not play until the viewer leaves an email. Reading chat is never blocked either way.'
	}
]

// What the viewer form links to when no custom privacy URL is set.
const CASTR_PRIVACY_URL = 'https://castr.com/privacypolicy/'

// What the player shows when a field is left blank, surfaced as placeholders
// so blank reads as "the default" rather than "nothing". Overriding them is
// how the form is localised — any language, no locale switch.
const COPY_FIELDS = [
	{ key: 'title', label: 'Form title', max: 120, fallback: 'Enter your email to continue' },
	{ key: 'nameLabel', label: 'Name field label', max: 40, fallback: 'Your name' },
	{ key: 'emailLabel', label: 'Email field label', max: 40, fallback: 'Email address' },
	{ key: 'buttonText', label: 'Button text', max: 30, fallback: 'Continue' },
	{ key: 'consentText', label: 'Consent text', max: 200, fallback: 'I agree that the host may contact me at this email.' }
]

const blank = () => ({
	mode: 'off',
	collectName: false,
	title: '',
	nameLabel: '',
	emailLabel: '',
	buttonText: '',
	consentText: '',
	privacyUrl: ''
})

/** The saved block with every field present, whatever era it was saved in. */
const saved = computed(() => {
	const block = props.emailCapture || {}
	return { ...blank(), ...block, privacyUrl: block.privacyUrl || '' }
})

const drafts = ref(blank())

// Resynced when a save lands, which is also what clears the dirty state
// without this component tracking it. Compared by content, not reference: the
// parent swaps the whole settings object on every persist — a feature toggle
// elsewhere on the card included — and only an actual change to this block
// should cost someone their half-written form.
let syncedSnapshot = null

watch(saved, (next) => {
	const snapshot = JSON.stringify(next)
	if (snapshot === syncedSnapshot) return

	syncedSnapshot = snapshot
	drafts.value = { ...next }
}, { immediate: true })

const normalize = (block) => ({
	mode: block.mode,
	collectName: !!block.collectName,
	title: (block.title || '').trim(),
	nameLabel: (block.nameLabel || '').trim(),
	emailLabel: (block.emailLabel || '').trim(),
	buttonText: (block.buttonText || '').trim(),
	consentText: (block.consentText || '').trim(),
	privacyUrl: (block.privacyUrl || '').trim() || null
})

const dirty = computed(() => {
	return JSON.stringify(normalize(drafts.value)) !== JSON.stringify(normalize(saved.value))
})

const expanded = computed(() => drafts.value.mode !== 'off')

const activeMode = computed(() => MODES.find((m) => m.key === drafts.value.mode) || MODES[0])

/* --------------------------- validation ------------------------------ */

const privacyUrlValid = computed(() => {
	const value = (drafts.value.privacyUrl || '').trim()
	if (!value) return false

	try {
		return new URL(value).protocol === 'https:'
	} catch (err) {
		return false
	}
})

// Blank is fine — the form then links to Castr's standard privacy policy.
// Only a custom link that is not real https is a problem worth blocking on.
const privacyUrlProblem = computed(() => {
	const value = (drafts.value.privacyUrl || '').trim()
	if (!value || privacyUrlValid.value) return ''
	return 'The privacy policy link must be a full https:// address. Leave it blank and the form links to Castr\'s standard policy instead.'
})

const canSave = computed(() => dirty.value && !privacyUrlProblem.value)

const save = () => {
	if (!canSave.value || props.saving) return
	emit('save', normalize(drafts.value))
}

const discard = () => {
	drafts.value = { ...saved.value }
}

/* ---------------------------- webhook -------------------------------- */

// Separate from the drafted block above: the webhook lives behind its own
// endpoints, not in the stream's settings, and the secret is write-only —
// the server only ever answers whether one is set.

const webhook = ref(null)
const webhookLoadError = ref('')
const webhookLoaded = ref(false)
const webhookSaving = ref(false)
const webhookUrlDraft = ref('')
const webhookSecretDraft = ref('')

// The switch that collapses the whole webhook config to one row. On means
// "show me the fields", nothing more — nothing is stored until Save. Off is
// where the teeth are: with a webhook stored, turning it off clears the
// stored config, so that path confirms first.
const webhookEnabled = ref(false)
const confirmingWebhookOff = ref(false)

const loadWebhook = async () => {
	try {
		const res = await AudienceService.getCapturesWebhook(props.streamId)
		webhook.value = { url: res.url || '', secretSet: !!res.secretSet }
		webhookUrlDraft.value = webhook.value.url
		// A stored URL is what "on" means; a fresh stream starts collapsed.
		webhookEnabled.value = !!webhook.value.url
		webhookLoadError.value = ''
	} catch (err) {
		webhookLoadError.value = err.message || 'Could not load the webhook settings'
	}
	webhookLoaded.value = true
}

// Fetched on first expansion rather than mount, so a stream with the feature
// off never pays for (or logs a 404 from) an endpoint it is not using.
watch(expanded, (open) => {
	if (open && !webhookLoaded.value) loadWebhook()
}, { immediate: true })

const webhookUrlValid = computed(() => {
	const value = webhookUrlDraft.value.trim()
	if (!value) return true

	try {
		return new URL(value).protocol === 'https:'
	} catch (err) {
		return false
	}
})

const webhookDirty = computed(() => {
	if (!webhook.value) return false
	return webhookUrlDraft.value.trim() !== webhook.value.url || !!webhookSecretDraft.value
})

// Removal has moved to the toggle, where it is confirmed; Save only ever
// stores a real endpoint, so an accidentally emptied field cannot clear a
// working integration.
const webhookSavable = computed(() => {
	return webhookDirty.value && webhookUrlValid.value && !!webhookUrlDraft.value.trim()
})

const saveWebhook = async () => {
	if (!webhookSavable.value || webhookSaving.value) return

	const url = webhookUrlDraft.value.trim()
	webhookSaving.value = true

	try {
		const payload = { url }
		// Left out entirely when blank, so saving a new URL never wipes the
		// secret that is already stored.
		if (webhookSecretDraft.value) payload.secret = webhookSecretDraft.value

		await AudienceService.setCapturesWebhook(props.streamId, payload)

		webhook.value = {
			url,
			secretSet: !!webhookSecretDraft.value || webhook.value.secretSet
		}
		webhookUrlDraft.value = url
		webhookSecretDraft.value = ''
		notify({ group: 'success', text: 'Webhook saved' })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not save the webhook' })
	}

	webhookSaving.value = false
}

const onWebhookToggle = () => {
	// v-model has already flipped the switch.
	if (webhookEnabled.value) {
		confirmingWebhookOff.value = false
		return
	}

	// Off with a webhook stored deletes a working integration, so the switch
	// springs back and asks first — the same shape as the anonymous-viewers
	// toggle on the card above.
	if (webhook.value && webhook.value.url) {
		webhookEnabled.value = true
		confirmingWebhookOff.value = true
		return
	}

	// Nothing stored: collapsing costs nothing but half-typed drafts.
	webhookUrlDraft.value = ''
	webhookSecretDraft.value = ''
}

const confirmWebhookOff = async () => {
	webhookSaving.value = true

	try {
		// An empty url is the clear operation server-side.
		await AudienceService.setCapturesWebhook(props.streamId, { url: '' })

		webhook.value = { url: '', secretSet: false }
		webhookUrlDraft.value = ''
		webhookSecretDraft.value = ''
		webhookEnabled.value = false
		confirmingWebhookOff.value = false
		notify({ group: 'success', text: 'Webhook removed' })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not remove the webhook' })
	}

	webhookSaving.value = false
}
</script>

<template>
	<div>
		<!-- Label + mode control share a row when there’s room; flex-wrap drops
		the control under the label on narrow widths so the label isn’t crushed.
		The per-mode hint stays below in a fixed min-height slot. -->
		<div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
			<p class="text-sm text-surface-text-1 mb-0">Ask viewers for their email</p>
			<div class="inline-flex flex-nowrap items-center gap-0.5 sm:gap-1 bg-surface-2 rounded-lg p-1"
				role="group" aria-label="Email capture mode">
				<button v-for="mode in MODES" :key="mode.key" type="button"
					class="text-sm font-medium px-2 sm:px-3 py-1.5 rounded-md whitespace-nowrap"
					:class="drafts.mode === mode.key
						? 'bg-surface-4 text-white shadow-sm'
						: 'text-surface-8 hover:text-white'"
					:aria-pressed="drafts.mode === mode.key" :disabled="saving"
					@click="drafts.mode = mode.key">
					{{ mode.label }}
				</button>
			</div>
		</div>
		<p class="text-tiny text-surface-7 min-h-8 mt-1 mb-0">{{ activeMode.hint }}</p>

		<template v-if="expanded">
			<div class="flex items-center justify-between gap-x-4 mt-4 py-3 border-t border-surface-2">
				<div>
					<p class="text-sm text-surface-text-1 mb-0.5">Also ask for a name</p>
					<p class="text-tiny text-surface-7 mb-0">
						Adds a name field to the form. The email is always asked for.
					</p>
				</div>
				<FormSwitch v-model="drafts.collectName" :disabled="saving" />
			</div>

			<div class="py-3 border-t border-surface-2">
				<p class="text-sm text-surface-text-1 mb-0.5">Form text</p>
				<p class="text-tiny text-surface-7 mb-3">
					Write the form in any language. Blank fields use the default shown in grey.
				</p>

				<div class="grid gap-3 sm:grid-cols-2">
					<template v-for="field in COPY_FIELDS" :key="field.key">
						<div v-if="field.key !== 'nameLabel' || drafts.collectName"
							:class="{ 'sm:col-span-2': field.key === 'title' || field.key === 'consentText' }">
							<FormInput v-model="drafts[field.key]" :label="field.label"
								:placeholder="field.fallback" :maxlength="field.max"
								:id="`email-capture-${field.key}`" :disabled="saving" />
							<p class="text-tiny text-surface-7 mt-1 mb-0">Up to {{ field.max }} characters.</p>
						</div>
					</template>
				</div>
			</div>

			<div class="py-3 border-t border-surface-2">
				<FormInput v-model="drafts.privacyUrl" label="Privacy policy link"
					:placeholder="CASTR_PRIVACY_URL" id="email-capture-privacy"
					:maxlength="2048" :disabled="saving" :is-error="!!privacyUrlProblem" />
				<p class="text-tiny text-surface-7 mt-1 mb-0">
					Linked from the form so viewers can read how their email is used. Optional: left
					blank, the form links to Castr's standard privacy policy, or set your own
					privacy policy link.
				</p>
			</div>
		</template>

		<Alert v-if="privacyUrlProblem" type="warning" class="mt-2" :text="privacyUrlProblem" />

		<div v-if="dirty" class="flex flex-wrap items-center justify-end gap-x-3 gap-y-2 mt-3">
			<p class="text-tiny text-surface-7 mb-0">Changes are not saved yet.</p>
			<FormButton type="secondary" size="sm" :disabled="saving" @click="discard">Discard</FormButton>
			<FormButton size="sm" :is-loading="saving" :disabled="!canSave" @click="save">
				Save email capture
			</FormButton>
		</div>

		<!-- Webhook: its own save, because it is its own storage — the URL and
		secret never travel through the stream's settings. Collapsed to one
		row behind the switch until someone wants it. -->
		<div v-if="expanded" class="mt-4 pt-4 border-t border-surface-3">
			<div class="flex items-center justify-between gap-x-4">
				<div>
					<p class="text-sm text-surface-text-1 mb-0.5">Delivery webhook</p>
					<p class="text-tiny text-surface-7 mb-0">
						Sends each new capture to your endpoint as it happens, so emails can land in your
						CRM or mailing list without polling.
					</p>
				</div>
				<FormSwitch v-model="webhookEnabled"
					:disabled="!webhookLoaded || !!webhookLoadError || webhookSaving"
					@change="onWebhookToggle" />
			</div>

			<Alert v-if="webhookLoadError" type="error" class="mt-3">
				<span class="flex flex-wrap items-center gap-x-2 gap-y-1">
					{{ webhookLoadError }}
					<button type="button" class="underline font-medium" @click="loadWebhook">
						Try again
					</button>
				</span>
			</Alert>

			<p v-else-if="!webhookLoaded" class="text-tiny text-surface-7 mt-2 mb-0">
				Loading webhook settings…
			</p>

			<!-- Asked while the switch is still on: the stored config survives
			until this is confirmed, so cancelling costs nothing. -->
			<div v-if="confirmingWebhookOff && webhook"
				class="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 bg-surface-2 rounded-lg px-3 py-2 mt-3">
				<p class="text-tiny text-surface-text-1 mb-0">
					Turning this off deletes the stored webhook and its secret — deliveries to
					{{ webhook.url }} stop immediately.
				</p>
				<div class="flex items-center gap-x-2">
					<FormButton type="secondary" size="sm" :disabled="webhookSaving"
						@click="confirmingWebhookOff = false">
						Keep it
					</FormButton>
					<FormButton type="red" size="sm" :is-loading="webhookSaving" @click="confirmWebhookOff">
						Remove webhook
					</FormButton>
				</div>
			</div>

			<template v-if="webhookEnabled && webhook">
				<div class="grid gap-3 sm:grid-cols-2 mt-3">
					<FormInput v-model="webhookUrlDraft" label="Endpoint URL"
						placeholder="https://example.com/hooks/captures" id="capture-webhook-url"
						:maxlength="2048" :disabled="webhookSaving" :is-error="!webhookUrlValid" />
					<div>
						<FormInput v-model="webhookSecretDraft" type="password" label="Signing secret"
							id="capture-webhook-secret" :maxlength="256" :disabled="webhookSaving"
							:placeholder="webhook.secretSet ? 'Leave blank to keep the current secret' : 'Optional'" />
						<!-- The secret is write-only server-side; this line is the
						only acknowledgement it exists. -->
						<p v-if="webhook.secretSet" class="text-tiny text-surface-7 mt-1 mb-0">
							A secret is set. It is never shown again; type a new one to replace it.
						</p>
					</div>
				</div>

				<p v-if="!webhookUrlValid" class="text-tiny text-error mt-2 mb-0">
					The endpoint must be a full https:// address.
				</p>

				<div class="flex items-center justify-between gap-x-3 mt-3">
					<p class="text-tiny text-surface-7 mb-0">
						<template v-if="webhook.url">Deliveries are going to {{ webhook.url }}</template>
						<template v-else>Nothing is stored until you save. The switch removes a saved webhook.</template>
					</p>
					<FormButton type="secondary" size="sm" :is-loading="webhookSaving"
						:disabled="!webhookSavable" @click="saveWebhook">
						Save webhook
					</FormButton>
				</div>
			</template>
		</div>
	</div>
</template>
